export interface CompressImageOptions {
  /** Longer edge is scaled down to this (px). Default: 1920 */
  maxWidthOrHeight?: number;
  /** Target ceiling in MB after compression. Default: 1.0 */
  maxSizeMB?: number;
  /** Starting JPEG quality (0–1). Default: 0.85 */
  initialQuality?: number;
}

export interface CompressImageResult {
  file: File;
  /** true when the original was replaced by a smaller version */
  wasCompressed: boolean;
  originalSizeMB: number;
  compressedSizeMB: number;
}

/**
 * Compresses an image file using the Canvas API — the same approach used by
 * Instagram, WhatsApp, and major food-delivery platforms.
 *
 * Steps:
 *  1. If the image is already within bounds, return it untouched.
 *  2. Scale the longest edge down to maxWidthOrHeight (aspect-ratio preserved).
 *  3. Re-encode as JPEG starting at initialQuality.
 *  4. If still over maxSizeMB, reduce quality by 0.08 per iteration (floor: 0.30).
 *  5. Return the compressed File with the original base-name + ".jpg".
 */
export async function compressImage(
  file: File,
  options: CompressImageOptions = {},
): Promise<CompressImageResult> {
  const { maxWidthOrHeight = 1920, maxSizeMB = 1.0, initialQuality = 0.85 } = options;

  const maxBytes = maxSizeMB * 1024 * 1024;
  const originalSizeMB = file.size / 1024 / 1024;

  const img = await loadImage(file);
  const { naturalWidth: w, naturalHeight: h } = img;

  const exceedsDimensions = w > maxWidthOrHeight || h > maxWidthOrHeight;
  const exceedsSize = file.size > maxBytes;

  if (!exceedsDimensions && !exceedsSize) {
    return { file, wasCompressed: false, originalSizeMB, compressedSizeMB: originalSizeMB };
  }

  // Scale maintaining aspect ratio
  const scale = exceedsDimensions ? maxWidthOrHeight / Math.max(w, h) : 1;
  const canvasW = Math.round(w * scale);
  const canvasH = Math.round(h * scale);

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;

  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // White background for transparent PNGs so JPEG conversion looks correct
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasW, canvasH);
  ctx.drawImage(img, 0, 0, canvasW, canvasH);

  let quality = initialQuality;
  let blob: Blob | null = null;

  do {
    blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (!blob || blob.size <= maxBytes) break;
    quality = Math.max(quality - 0.08, 0.30);
  } while (blob && blob.size > maxBytes && quality > 0.30);

  // Safety fallback: original file if canvas produced nothing
  if (!blob) {
    return { file, wasCompressed: false, originalSizeMB, compressedSizeMB: originalSizeMB };
  }

  const baseName = file.name.replace(/\.[^/.]+$/, '');
  const compressed = new File([blob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });

  return {
    file: compressed,
    wasCompressed: true,
    originalSizeMB,
    compressedSizeMB: compressed.size / 1024 / 1024,
  };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Görsel yüklenemedi'));
    };
    img.src = url;
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}
